<template>
    <div class="ui-page">
        <ui-page-header :title="page.title" back="/procuradores" back-text="Procuradores">
            <v-btn id="btnSave" type="button" color="primary" depressed @click="add"
                :loading="dataForm.addLoading" v-if="(page.isNew) && $allowed('save')">Salvar</v-btn>
            <v-btn id="btnSave" type="button" color="primary" depressed @click="update"
                :loading="dataForm.updateLoading" v-if="(!page.isNew) && $allowed('save')">Salvar</v-btn>
        </ui-page-header>

        <!-- Validacao interna do cadastro, como no credor: sem verificacao o procurador nao consegue entrar no site -->
        <v-banner v-if="!page.isNew && dataset.data.id && !isFilledDate(dataset.data.account_verified_at)" single-line outlined class="mb-4">
            <v-icon slot="icon" color="warning" size="36">
                mdi-alert-outline
            </v-icon>
            Este procurador ainda não verificou o cadastro pelo e-mail e não consegue acessar o site.

            <template v-slot:actions>
                <v-btn color="primary" text :loading="dataForm.verifyLoading" @click="verifyAccount()">
                    VALIDAR CADASTRO
                </v-btn>
            </template>
        </v-banner>
        <v-banner v-if="!page.isNew && isFilledDate(dataset.data.account_verified_at)" single-line outlined class="mb-4">
            <v-icon slot="icon" color="success" size="36">
                mdi-check-circle-outline
            </v-icon>
            Cadastro validado em {{ dataset.data.account_verified_at | defaultDate("DD/MM/YYYY HH:mm") }}.
        </v-banner>

        <v-form :readonly="!$allowed('save')" ref="form" v-model="dataForm.validForm" class="mb-4">
            <ui-section title="Detalhes">
                <v-row>
                    <v-col>
                        <v-text-field dense id="name" label="Nome procurador" v-model="dataset.data.name" required
                            :rules="[$rules.required]" outlined hide-details="auto">
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field dense id="registration" label="CPF" v-model="dataset.data.registration" required
                            v-mask="$masks.cpf" :rules="[$rules.required]" outlined hide-details="auto">
                        </v-text-field>
                    </v-col>
                    <v-col>
                        <v-text-field dense id="procurator_email" label="E-mail" v-model="dataset.data.email" required
                            :rules="[$rules.required]" outlined hide-details="auto">
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field dense id="oab" label="OAB (se advogado)" v-model="dataset.data.oab"
                            :rules="dataset.data.oab_state ? [$rules.required] : []" outlined hide-details="auto">
                        </v-text-field>
                    </v-col>
                    <v-col>
                        <v-autocomplete :disabled="dataset.loading" dense :items="dataset.statesList"
                            label="UF da OAB" v-model="dataset.data.oab_state" item-value="name"
                            item-text="name" :rules="dataset.data.oab ? [$rules.required] : []" outlined hide-details="auto">
                        </v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-file-input dense accept="image/png, image/jpeg, application/pdf" prepend-icon="" outlined
                            counter :rules="[ $rules.fileSize(4)]" show-size
                            hint="O arquivo não pode ser superior a 4MB" hide-details="auto"
                            label="Documento de ID do procurador" :clearable="true" @change="onProcuratorSelected($event)" ref="procurator_file">
                        </v-file-input>
                    </v-col>
                </v-row>

                <v-row v-if="dataset.data.document_url">
                    <v-col cols="12">
                        <v-btn type="button" color="primary" outlined :href="dataset.data.document_url"
                            target="_blank">Visualizar documento
                        </v-btn>
                    </v-col>
                </v-row>
            </ui-section>
        </v-form>

        <ui-section v-if="!page.isNew" title="Associações de credores" flush>
            <template #actions>
                <v-btn small color="primary" outlined :loading="associations.loading" @click="loadAssociations">
                    Atualizar
                </v-btn>
            </template>
            <v-data-table
                :headers="associations.headers"
                :items="associations.data"
                :server-items-length="associations.count"
                :options.sync="associations.options"
                :loading="associations.loading"
                disable-sort
                hide-default-footer
                no-data-text="Nenhuma associação encontrada"
                loading-text="Carregando associações..."
            >
                <template v-slot:[`item.creditor_name`]="{ item }">
                    <div class="ui-cell-main">{{ item.creditor_name }}</div>
                    <div class="ui-cell-sub">{{ formatRegistration(item.creditor_registration) }}</div>
                </template>

                <template v-slot:[`item.client`]="{ item }">
                    {{ item.client ? item.client.name : 'N/A' }}
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="associationStatusText(item.status)" :color="associationStatusColor(item.status)" />
                </template>

                <!-- Data original do pedido: mantida quando o procurador complementa -->
                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                    <div v-if="item.regularized_at" class="ui-cell-sub">Complementado em {{ item.regularized_at | defaultDate("DD/MM/YYYY HH:mm") }}</div>
                </template>

                <template v-slot:[`item.rejection_reason`]="{ item }">
                    <span v-if="item.rejection_reason">{{ item.rejection_reason }}</span>
                    <div v-else-if="item.regularized_at && item.regularization_reason" class="ui-cell-sub">Pedido anterior: {{ item.regularization_reason }}</div>
                    <span v-else class="ui-muted">-</span>
                </template>

                <template v-slot:[`item.reviewed_at`]="{ item }">
                    {{ item.reviewed_at ? $options.filters.defaultDate(item.reviewed_at, "DD/MM/YYYY HH:mm") : "N/A" }}
                </template>

                <template v-slot:[`item.documents`]="{ item }">
                    <div class="d-flex align-center" style="gap: 6px;">
                        <v-btn v-if="item.creditor_document_url" icon small :href="item.creditor_document_url" target="_blank">
                            <v-icon size="18" class="ui-cell-chevron">mdi-card-account-details-outline</v-icon>
                        </v-btn>
                        <v-btn v-if="item.procuration_document_url" icon small :href="item.procuration_document_url" target="_blank">
                            <v-icon size="18" class="ui-cell-chevron">mdi-file-document-outline</v-icon>
                        </v-btn>
                        <!-- Procuracao gerada pelo modelo: versao e validacao -->
                        <procuration-source-badge v-if="item.procuration" :procuration="item.procuration" />
                    </div>
                </template>

                <template v-slot:[`item.procuration_powers`]="{ item }">
                    <div v-if="item.procuration_powers && item.procuration_powers.length" class="d-flex flex-wrap" style="gap: 4px;">
                        <v-chip v-for="power in item.procuration_powers" :key="power" x-small label outlined color="primary">{{ powerLabel(power) }}</v-chip>
                    </div>
                    <span v-else class="ui-small ui-muted">Poderes não informados</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex align-center justify-end" style="gap: 4px;">
                        <ai-analysis-button
                            :endpoint="`/api/admin/ai/procurator-associations/${item.id}/analyze`"
                            label="Analisar associação com IA"
                            title="Análise IA da associação"
                            icon-only
                        />
                        <ui-row-actions :actions="[
                            { text: 'Aprovar', icon: 'mdi-check-circle-outline', disabled: Number(item.status) === 1, handler: () => approveAssociation(item) , hidden: !$allowed('procurator.associations') },
                            { text: 'Solicitar regularização', icon: 'mdi-file-document-refresh-outline', disabled: [1, 2, 3].includes(Number(item.status)), handler: () => openRejectAssociationDialog(item, 'regularize'), hidden: !$allowed('procurator.associations') },
                            { text: 'Rejeitar', icon: 'mdi-close-circle-outline', danger: true, disabled: Number(item.status) === 2, handler: () => openRejectAssociationDialog(item) },
                        ]" />
                    </div>
                </template>
            </v-data-table>
            <ui-pagination :total="associations.count" :page="associations.options.page" :items-per-page="associations.options.itemsPerPage"
                :disabled="associations.loading" @update:page="associationPageChanged" @update:itemsPerPage="setAssociationPageSize" />
        </ui-section>

        <v-dialog v-model="rejectAssociationDialog.open" max-width="600px" persistent>
            <div class="ui-card">
                <header class="ui-card__header">
                    <h3 class="ui-section-title">{{ rejectAssociationDialog.mode === 'regularize' ? 'Solicitar regularização' : 'Rejeitar associação' }}</h3>
                </header>
                <div class="ui-card__body">
                    <div class="mb-4 text-body-2">
                        {{ rejectAssociationDialog.mode === 'regularize' ? 'Informe o que o procurador deve complementar no pedido de associação com o credor' : 'Informe o motivo da rejeição para a associação com o credor' }}
                        <strong>{{ rejectAssociationDialog.item ? rejectAssociationDialog.item.creditor_name : '' }}</strong>.
                    </div>
                    <v-textarea
                        v-model="rejectAssociationDialog.reason"
                        :label="rejectAssociationDialog.mode === 'regularize' ? 'O que deve ser regularizado' : 'Motivo da rejeição'"
                        outlined
                        auto-grow
                        rows="3"
                        hide-details="auto"
                    />
                    <v-alert v-if="rejectAssociationDialog.mode === 'regularize'" dense text type="info" class="mt-4 mb-0 text-body-2">
                        O procurador recebe o pedido por e-mail e complementa este mesmo pedido em "Complementar". A data original da solicitação é mantida.
                    </v-alert>
                </div>
                <footer class="ui-card__footer">
                    <v-btn text :disabled="rejectAssociationDialog.loading" @click="closeRejectAssociationDialog">
                        Cancelar
                    </v-btn>
                    <v-btn v-if="$allowed('procurator.associations')" color="primary" depressed :loading="rejectAssociationDialog.loading" @click="confirmRejectAssociation">
                        {{ rejectAssociationDialog.mode === 'regularize' ? 'Solicitar regularização' : 'Rejeitar' }}
                    </v-btn>
                </footer>
            </div>
        </v-dialog>
    </div>
</template>

<script>
import procurationPowers from "@/common/data/procuration-powers.data";

import axios from "axios"
import statesList from "@/common/data/states-list.data"
import AIAnalysisButton from "@/components/AIAnalysisButton.vue"
import ProcurationSourceBadge from "@/components/ProcurationSourceBadge.vue"

export default {
    name: "ProcuratorDetails",

    components: {
        'ai-analysis-button': AIAnalysisButton,
        ProcurationSourceBadge,
    },

    data: () => ({
        page: {
            isNew: false,
            title: "",
        },
        dataset: {
            breadcrumbItems: [],
            data: {},
            modalResult: null,
            statesList: statesList,
        },
        dataForm: {
            validForm: true,
            addLoading: false,
            updateLoading: false,
            removeLoading: false,
            showPassword: false,
            showPasswordConfirmation: false,
        },
        associations: {
            loading: false,
            data: [],
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            },
            headers: [
                { text: "Credor", value: "creditor_name" },
                { text: "Caso", value: "client" },
                { text: "Solicitado em", value: "created_at" },
                { text: "Status", value: "status" },
                { text: "Motivo", value: "rejection_reason" },
                { text: "Analisado em", value: "reviewed_at" },
                { text: "Documentos", value: "documents", sortable: false },
                { text: "Poderes", value: "procuration_powers", sortable: false },
                { text: "", value: "actions", sortable: false, align: "end", width: 96 },
            ],
        },
        rejectAssociationDialog: {
            open: false,
            loading: false,
            item: null,
            reason: "",
            mode: "reject",
        },
    }),

    created() {
        this.loadData()
    },
   
    methods: {
        powerLabel(id) {
            const found = procurationPowers.find((item) => item.id === id);
            return found ? found.name : id;
        },

        goBack() {
            this.$router.replace("/procuradores");
        },

        async loadData() {
            this.setPage();
            this.listBreadCrumbItems();
            await this.load();
            await this.loadAssociations();
        },

        setPage() {
            this.page.isNew = this.$route.params.id === "novo";
            this.page.title = `${this.page.isNew ? 'Novo procurador' : 'Detalhes do procurador'}`;
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                {
                    text: "Homepage",
                    disabled: false,
                    to: "/home",
                    exact: true,
                },
            ];

            this.dataset.breadcrumbItems.push({
                text: "Procuradores",
                disabled: false,
                to: "/procuradores",
                exact: true,
            })

            this.dataset.breadcrumbItems.push({
                text: this.page.title,
                disabled: true,
                exact: true,
            });
        },

        onProcuratorSelected(procurator_file) {
            this.dataset.data.procurator_file = procurator_file;
        },

        isFilledDate(value) {
            return !!value && value !== '0000-00-00 00:00:00' && value !== '0000-00-00';
        },

        // Mesma rota usada para validar a conta do credor.
        async verifyAccount() {
            const proceed = await this.$dialog.confirm({
                title: 'Validar cadastro',
                text: 'Validar internamente o cadastro deste procurador? Ele passará a conseguir acessar o site sem confirmar o e-mail.',
                actions: { false: 'Cancelar', true: 'Validar' },
            });
            if (!proceed) return;

            this.$set(this.dataForm, 'verifyLoading', true);
            try {
                await axios.post(`/api/admin/users/verify-account/${this.$route.params.id}`, {});
                this.$dialog.notify.info('Cadastro validado com sucesso.', { position: 'top-right', timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
            } finally {
                this.$set(this.dataForm, 'verifyLoading', false);
            }
        },

        async load() {

            if (this.page.isNew) return;

            try {
                const result = await axios.get(`/api/admin/procurators/${this.$route.params.id}`);
                

                const data = result.data.content;

                this.dataset.data = data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },


        async add() {
            if (!this.$refs.form.validate()) return;

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja incluir o procurador?',
                title: 'Incluir procurador',
                actions: {
                    false: 'Cancelar',
                    true: 'Salvar'
                }
            })

            if (!proceedAction) return;

            this.dataForm.addLoading = true;

            const payload = this.buildPayload();

            try {
                await axios.post('/api/admin/procurators', payload);

                this.$dialog.notify.info("Procurador salvo com sucesso!", { position: "top-right", timeout: 2000 });

                this.goBack()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataForm.addLoading = false;
            }

        },

        async update() {
            if (!this.$refs.form.validate()) return;

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja atualizar dados do procurador?',
                title: 'Atualizar dados procurador',
                actions: {
                    false: 'Cancelar',
                    true: 'Atualizar'
                }
            })

            if (!proceedAction) return;

            this.dataForm.updateLoading = true;

            const payload = this.buildPayload();

            try {
                await axios.patch(`/api/admin/procurators/${this.$route.params.id}`, payload);

                this.$dialog.notify.info("Procurador atualizado com sucesso!", { position: "top-right", timeout: 2000 });

                this.goBack()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataForm.updateLoading = false;
            }
        },

       
        buildPayload() {
            const formData = new FormData()

            if (this.dataset.data.name)
                formData.append('name', this.dataset.data.name);

            if (this.dataset.data.registration)
                formData.append('registration', String(this.dataset.data.registration).replace(/\D/g, ''))

            formData.append('oab', this.dataset.data.oab || '')
            formData.append('oab_state', this.dataset.data.oab_state || '')

            if (this.dataset.data.email)
                formData.append('email', this.dataset.data.email)

            if (this.dataset.data.procurator_file)
                formData.append('procurator_document', this.dataset.data.procurator_file)

            return formData;
        },

        associationPageChanged(page) {
            this.associations.options.page = page;
            this.loadAssociations();
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setAssociationPageSize(size) {
            this.associations.options.itemsPerPage = size;
        },

        associationPageSizeChanged(size) {
            this.associations.options.itemsPerPage = size;
            this.loadAssociations();
        },

        async loadAssociations() {
            if (this.page.isNew) return;

            this.associations.loading = true;

            try {
                const search = {
                    procurator_user_id: this.$route.params.id,
                };

                const result = await axios.get('/api/admin/procurator-associations', {
                    params: {
                        page: this.associations.options.page || 1,
                        limit: this.associations.options.itemsPerPage || 10,
                        search,
                    },
                });

                this.associations.data = result.data.content.data;
                this.associations.count = result.data.content.paging.total;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 3000
                });
            } finally {
                this.associations.loading = false;
            }
        },

        async approveAssociation(item) {
            if (Array.isArray(item.authorized_procurators) && item.authorized_procurators.length) {
                this.$dialog.notify.error("Esta associação possui procuradores adicionais. Aprove pelo bloco de associações pendentes para revisar os nomes autorizados.", {
                    position: 'top-right',
                    timeout: 5000
                });
                return;
            }

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja aprovar esta associação entre procurador e credor?',
                title: 'Aprovar associação',
                actions: {
                    false: 'Cancelar',
                    true: 'Aprovar'
                }
            });

            if (!proceedAction) return;

            const replaceExisting = await this.$dialog.confirm({
                text: 'Deseja substituir os procuradores já aprovados para este credor neste caso? Para manter os existentes e adicionar este procurador, escolha Adicionar.',
                title: 'Tipo de aprovação',
                actions: {
                    false: 'Adicionar',
                    true: 'Substituir'
                }
            });

            this.associations.loading = true;

            try {
                await axios.post(`/api/admin/procurator-associations/${item.id}/approve`, {
                    approval_mode: replaceExisting ? 'replace' : 'add',
                    replace_existing: !!replaceExisting,
                });

                this.$dialog.notify.info("Associação aprovada com sucesso!", { position: "top-right", timeout: 2000 });
                await this.loadAssociations();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 3000
                });
            } finally {
                this.associations.loading = false;
            }
        },

        openRejectAssociationDialog(item, mode = "reject") {
            this.rejectAssociationDialog.item = item;
            this.rejectAssociationDialog.mode = mode;
            this.rejectAssociationDialog.reason = "";
            this.rejectAssociationDialog.open = true;
        },

        closeRejectAssociationDialog() {
            if (this.rejectAssociationDialog.loading) return;
            this.rejectAssociationDialog.open = false;
            this.rejectAssociationDialog.item = null;
            this.rejectAssociationDialog.reason = "";
        },

        async confirmRejectAssociation() {
            if (!this.rejectAssociationDialog.item) return;
            const regularize = this.rejectAssociationDialog.mode === "regularize";
            if (!this.rejectAssociationDialog.reason || !this.rejectAssociationDialog.reason.trim()) {
                this.$dialog.notify.error(regularize ? "Informe o que deve ser regularizado" : "Motivo da rejeição é obrigatório", {
                    position: 'top-right',
                    timeout: 3000
                });
                return;
            }

            this.rejectAssociationDialog.loading = true;

            try {
                await axios.post(`/api/admin/procurator-associations/${this.rejectAssociationDialog.item.id}/${regularize ? "request-regularization" : "reject"}`, {
                    [regularize ? "reason" : "rejection_reason"]: this.rejectAssociationDialog.reason.trim(),
                });

                this.$dialog.notify.info(regularize ? "Regularização solicitada ao procurador." : "Associação rejeitada com sucesso!", { position: "top-right", timeout: 2000 });
                this.closeRejectAssociationDialog();
                await this.loadAssociations();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 3000
                });
            } finally {
                this.rejectAssociationDialog.loading = false;
            }
        },

        associationStatusText(status) {
            const normalizedStatus = Number(status);
            if (normalizedStatus === 1) return "Aprovada";
            if (normalizedStatus === 2) return "Rejeitada";
            if (normalizedStatus === 3) return "Regularização solicitada";
            return "Pendente";
        },

        associationStatusColor(status) {
            const normalizedStatus = Number(status);
            if (normalizedStatus === 1) return "success";
            if (normalizedStatus === 2) return "error";
            if (normalizedStatus === 3) return "purple";
            return "warning";
        },

        formatRegistration(registration) {
            const value = String(registration || "").replace(/\D/g, "");
            if (value.length === 11) {
                return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
            }
            if (value.length === 14) {
                return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
            }
            return registration || "N/A";
        },

    },
};
</script>

<style lang="scss">
.access-container {
    border: 1px solid var(--v-gray-base) !important;
}

.custom-chip {
    background: var(--v-white-base) !important;
}

.title-page {
    color: var(--v-primary-base);
    // font-family: Montserrat;
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}

.section-info-title {
    color: var(--v-primary-base);
    // font-family: Montserrat;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
}

.section-info-value {
    // font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
}

.dialog-title {
    color: var(--v-primary-base);
    font-weight: 700;
}
</style>
